import React from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Building2,
  Users,
  TrendingUp,
  Shield,
  CheckCircle,
  ArrowRight,
  Star,
  Trophy,
} from "lucide-react";
import { clinicData } from "../mock";
import { useNavigate } from "react-router-dom";

const CorporateWellnessSection = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate("/book-appointment");
  const benefits = [
    {
      icon: TrendingUp,
      title: "Increased Productivity",
      description:
        "Reduce workplace injuries and improve employee performance by up to 25%",
    },
    {
      icon: Shield,
      title: "Lower Insurance Costs",
      description:
        "Proactive wellness programs can reduce healthcare costs and insurance premiums",
    },
    {
      icon: Users,
      title: "Enhanced Morale",
      description:
        "Show employees you care about their wellbeing and boost retention rates",
    },
    {
      icon: Trophy,
      title: "Competitive Advantage",
      description: "Attract top talent with comprehensive wellness benefits",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#204080]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 bg-[#204080]/10 text-[#204080] px-5 py-3 rounded-full text-base font-semibold mb-6">
            <Building2 size={20} />
            <span>Corporate Wellness</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#3a5bb8] drop-shadow-sm mb-4 tracking-tight leading-snug font-serif">
            Workplace Wellness Programs That Work
            {/* <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
              Programs That Work
            </span> */}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Invest in your team's health and productivity with our comprehensive
            corporate wellness programs designed for companies of all sizes.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <Card
                key={index}
                className="group p-6 text-center bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="space-y-4">
                  <div className="inline-flex p-3 bg-gradient-to-br from-[#204080] to-[#2E5AAC] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <IconComponent size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#204080] group-hover:text-[#2E5AAC] transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>

                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
              </Card>
            );
          })}
        </div>

        {/* Package Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {clinicData.corporatePackages.map((pkg, index) => (
            <Card
              key={pkg.id}
              className={`group relative overflow-hidden ${
                pkg.popular ? "ring-2 ring-[#2E5AAC] scale-105" : ""
              } bg-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
                  <Badge className="bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 px-2 py-0.5 shadow-lg rounded-full text-xs font-semibold flex items-center">
                    <Star size={10} className="mr-1" />
                    <span style={{ fontSize: "0.75rem" }}>Most Popular</span>
                  </Badge>
                </div>
              )}

              {/* Header */}
              <div
                className={`p-8 ${
                  pkg.popular
                    ? "bg-gradient-to-br from-[#204080] to-[#2E5AAC] text-white"
                    : "bg-gradient-to-br from-slate-50 to-blue-50"
                }`}
              >
                <div className="text-center space-y-4">
                  <h3
                    className={`text-2xl font-bold ${
                      pkg.popular ? "text-white" : "text-[#204080]"
                    }`}
                  >
                    {pkg.size}
                  </h3>
                  <p
                    className={`text-sm ${
                      pkg.popular ? "text-blue-100" : "text-gray-600"
                    }`}
                  >
                    {pkg.employees}
                  </p>
                  <div
                    className={`text-lg font-semibold ${
                      pkg.popular ? "text-blue-100" : "text-[#204080]"
                    }`}
                  >
                    {pkg.price}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="p-8 space-y-6">
                <div className="space-y-4">
                  {pkg.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="flex items-center space-x-3"
                    >
                      <CheckCircle
                        size={16}
                        className="text-[#2E5AAC] flex-shrink-0"
                      />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular
                      ? "bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white hover:shadow-xl transform hover:scale-105"
                      : "border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white"
                  }`}
                  onClick={handleBookAppointment}
                >
                  Get Started
                </button>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </Card>
          ))}
        </div>

        {/* Success Metrics */}
        <Card className="p-8 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">
              Proven Results for Corporate Partners
            </h3>
            <p className="text-blue-100 text-lg">
              Our corporate wellness programs deliver measurable results
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">85%</div>
              <div className="text-blue-100 text-sm">
                Reduction in Workplace Injuries
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">92%</div>
              <div className="text-blue-100 text-sm">
                Employee Participation Rate
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">30%</div>
              <div className="text-blue-100 text-sm">Decrease in Sick Days</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">150+</div>
              <div className="text-blue-100 text-sm">Companies Served</div>
            </div>
          </div>
        </Card>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
            <h3 className="text-2xl font-bold text-[#204080] mb-4">
              Ready to Transform Your Workplace Wellness?
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              Schedule a consultation to discuss how our corporate wellness
              programs can benefit your organization
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="flex items-center justify-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                onClick={handleBookAppointment}
              >
                <span>Schedule Corporate Consultation</span>
                <ArrowRight size={16} />
              </button>
              <button
                className="px-8 py-3 border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white font-semibold rounded-lg transition-all duration-300"
                onClick={() => {
                  // Create dummy content
                  const dummyContent =
                    "This is a dummy corporate wellness brochure.";
                  const blob = new Blob([dummyContent], { type: "text/plain" }); // plain text file
                  const url = URL.createObjectURL(blob);

                  // Create a temporary link to download the file
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "Corporate_Wellness_Brochure.txt"; // downloaded file name
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);

                  // Release the URL
                  URL.revokeObjectURL(url);
                }}
              >
                Download Brochure
              </button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CorporateWellnessSection;
